<script setup lang="ts">
import {computed, ref, watch} from "vue";
import {Check, CirclePlus, Pencil, UserRound, Users} from "@lucide/vue";
import dateHelper from "@/core/helpers/dateHelper";
import {Button} from "@/core/components/ui/button";
import {Input} from "@/core/components/ui/input";
import {Label} from "@/core/components/ui/label";
import {Textarea} from "@/core/components/ui/textarea";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/core/components/ui/select";
import GiftPalettePicker from "@/birthday-gift/components/GiftPalettePicker.vue";
import GiftProfileChips from "@/birthday-gift/components/GiftProfileChips.vue";
import GiftAutoSendSection from "@/birthday-gift/components/GiftAutoSendSection.vue";
import GiftVideoCard from "@/birthday-gift/components/GiftVideoCard.vue";
import {
    AGE_BAND_LABELS,
    GIFT_NAME_EXAMPLES,
    giftProfileLabel,
    SEX_LABELS,
    VALIDITY_DAYS
} from "@/birthday-gift/constants/palettes";
import {
    BirthdayGift,
    BirthdayGiftRequest,
    GiftAgeBand,
    GiftPalette,
    GiftSendHour,
    GiftSex,
    GiftValidityDays,
    GiftVideo
} from "@/birthday-gift/interfaces";

const props = defineProps<{
    /** The gift being edited, or null when creating one. */
    gift: BirthdayGift | null;
    sendHour: GiftSendHour;
    conflictMessage: string | null;
    saving: boolean;
}>();

const emit = defineEmits<{
    /** The send hour travels with the gift: it is only persisted on save. */
    (e: 'submit', payload: BirthdayGiftRequest, sendHour: GiftSendHour): void;
    (e: 'cancel'): void;
    (e: 'change', payload: BirthdayGiftRequest): void;
}>();

const name = ref('');
const counterNote = ref('');
const validityDays = ref<GiftValidityDays>(15);
const palette = ref<GiftPalette>('violet');
const ageBand = ref<GiftAgeBand>('all');
const sex = ref<GiftSex>('all');
const autoSend = ref(false);
const video = ref<GiftVideo>({video_type: 'stock', video_id: null, video_url: null});
const localSendHour = ref<GiftSendHour>(props.sendHour);

// The hour is a pharmacy-wide setting edited from here: keep a local copy so
// « Annuler » leaves it untouched, and follow it when it changes elsewhere.
watch(() => props.sendHour, (hour) => {
    localSendHour.value = hour;
});

/**
 * Load the edited gift into the form, or reset it for a creation.
 *
 * Keyed on the identity of the gift, not on the object: the list is
 * refetched after every write, and reloading here would wipe what the
 * pharmacist is currently typing.
 */
watch(() => props.gift?.id, () => {
    const gift = props.gift;

    name.value = gift?.name ?? '';
    counterNote.value = gift?.counter_note ?? '';
    validityDays.value = gift?.validity_days ?? 15;
    palette.value = gift?.palette ?? 'violet';
    ageBand.value = gift?.age_band ?? 'all';
    sex.value = gift?.sex ?? 'all';
    autoSend.value = gift?.auto_send ?? false;
    video.value = {
        video_type: gift?.video_type ?? 'stock',
        video_id: gift?.video_id ?? null,
        video_url: gift?.video_url ?? null,
    };
}, {immediate: true});

/**
 * The same gift can be switched to automatic from its card while the form is
 * open: mirror it here, otherwise saving the form would silently undo it.
 */
watch(() => props.gift?.auto_send, (value) => {
    if (value !== undefined) {
        autoSend.value = value;
    }
});

const payload = computed<BirthdayGiftRequest>(() => ({
    name: name.value.trim(),
    counter_note: counterNote.value.trim() || null,
    validity_days: validityDays.value,
    palette: palette.value,
    age_band: ageBand.value,
    sex: sex.value,
    auto_send: autoSend.value,
    ...video.value,
}));

// Keep the phone preview in sync with what is being typed.
watch(payload, (value) => emit('change', value), {immediate: true, deep: true});

const expiryLabel = computed(() => {
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + validityDays.value);

    return dateHelper.shortFormat(expiry, 'D MMMM');
});

const profileSummary = computed(() => giftProfileLabel(ageBand.value, sex.value) === 'Tous les patients'
    ? 'Ce cadeau peut être proposé à tous les patients.'
    : `Proposé en priorité aux patients : ${giftProfileLabel(ageBand.value, sex.value)}.`);

const ageBandOptions = (Object.keys(AGE_BAND_LABELS) as GiftAgeBand[])
    .map((value) => ({value, label: AGE_BAND_LABELS[value]}));

const sexOptions = (Object.keys(SEX_LABELS) as GiftSex[]).map((value) => ({
    value,
    label: SEX_LABELS[value],
    icon: value === 'all' ? Users : UserRound,
}));

const canSubmit = computed(() => name.value.trim().length > 0 && !props.saving);
</script>

<template>
  <div class="flex flex-col gap-4 rounded-lg bg-white p-5 shadow">
    <div class="flex flex-wrap items-center gap-2">
      <component
        :is="gift ? Pencil : CirclePlus"
        class="h-5 w-5 text-primary"
      />
      <h2 class="text-lg font-medium">
        {{ gift ? 'Modifier ce cadeau' : 'Nouveau cadeau' }}
      </h2>
      <span class="text-xs text-muted-foreground">
        {{ gift ? 'Les modifications s\'appliquent aux prochains envois' : 'Il apparaîtra dans vos cadeaux dès l\'enregistrement' }}
      </span>
    </div>

    <div>
      <Label>Le cadeau — visible par vous et votre équipe uniquement</Label>
      <Input
        v-model="name"
        placeholder="Une bougie Roger &amp; Gallet offerte"
        class="mt-1.5 w-full"
      />
      <div class="mt-2 flex flex-wrap items-center gap-2">
        <span class="text-xs text-muted-foreground">Exemples :</span>
        <button
          v-for="example in GIFT_NAME_EXAMPLES"
          :key="example"
          type="button"
          class="rounded-full border border-solid border-gray bg-white px-2.5 py-1 text-xs text-primary hover:border-primary"
          @click="name = example"
        >
          {{ example }}
        </button>
      </div>
    </div>

    <div>
      <Label>Consigne pour l'équipe au comptoir</Label>
      <Textarea
        v-model="counterNote"
        :rows="3"
        placeholder="Où trouver le produit, quoi saisir en caisse…"
        class="mt-1.5 w-full"
      />
      <div class="mt-1.5 text-xs leading-relaxed text-muted-foreground">
        Ce texte s'affiche à l'écran quand l'équipe scanne le QR code. Il n'est jamais visible par le patient.
      </div>
    </div>

    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div>
        <Label>Validité du cadeau</Label>
        <Select
          :model-value="String(validityDays)"
          @update:model-value="validityDays = Number($event) as GiftValidityDays"
        >
          <SelectTrigger class="mt-1.5 w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="days in VALIDITY_DAYS"
              :key="days"
              :value="String(days)"
            >
              {{ days }} jours
            </SelectItem>
          </SelectContent>
        </Select>
        <div class="mt-1.5 text-xs text-muted-foreground">
          Expire le {{ expiryLabel }} pour un anniversaire aujourd'hui
        </div>
      </div>
      <div>
        <Label>Repère visuel (pour vous)</Label>
        <GiftPalettePicker
          v-model="palette"
          class="mt-1.5"
        />
      </div>
    </div>

    <div class="border-t border-gray-light pt-4">
      <div class="mb-2 flex flex-wrap items-baseline gap-2">
        <Label>Profil de patients</Label>
        <span class="text-xs text-muted-foreground">optionnel — sinon proposé à tous</span>
      </div>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <div class="mb-1.5 text-xs text-muted-foreground">
            Tranche d'âge
          </div>
          <GiftProfileChips
            :model-value="ageBand"
            :options="ageBandOptions"
            @update:model-value="ageBand = $event as GiftAgeBand"
          />
        </div>
        <div>
          <div class="mb-1.5 text-xs text-muted-foreground">
            Sexe
          </div>
          <GiftProfileChips
            :model-value="sex"
            :options="sexOptions"
            @update:model-value="sex = $event as GiftSex"
          />
        </div>
      </div>
      <div class="mt-2 text-xs text-muted-foreground">
        {{ profileSummary }}
      </div>
    </div>

    <GiftVideoCard v-model="video" />

    <GiftAutoSendSection
      :auto-send="autoSend"
      :send-hour="localSendHour"
      :conflict-message="conflictMessage"
      @update:auto-send="autoSend = $event"
      @update:send-hour="localSendHour = $event"
    />

    <div class="flex flex-wrap justify-end gap-2 border-t border-gray-light pt-4">
      <Button
        variant="ghost"
        :disabled="saving"
        @click="emit('cancel')"
      >
        Annuler
      </Button>
      <Button
        :disabled="!canSubmit"
        @click="emit('submit', payload, localSendHour)"
      >
        <Check class="h-4 w-4" />{{ gift ? 'Enregistrer' : 'Créer le cadeau' }}
      </Button>
    </div>
  </div>
</template>
